import { Groq } from 'groq-sdk';

// Initialize Groq client for client-side usage
const getGroqClient = () => {
  const apiKey = import.meta.env.VITE_GROQ_API_KEY;
  
  if (!apiKey) {
    throw new Error('GROQ API key is not configured. Please check your environment variables.');
  }
  
  return new Groq({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Allow browser usage
  });
};

export interface AIResponse {
  suggestion?: string;
  formattedText?: string;
  summary?: string;
  error?: string;
}

export const getSuggestion = async (text: string, context?: string): Promise<AIResponse> => {
  try {
    if (!text || text.trim().length === 0) {
      throw new Error('Please provide text to get suggestions');
    }

    if (text.length > 32000) {
      throw new Error('Text is too long. Please use shorter content.');
    }

    const groq = getGroqClient();
    
    const completion = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are DocHelper AI, a professional document assistant focused on improving text while maintaining meaning and tone. Be concise and helpful."
        },
        {
          role: "user",
          content: context 
            ? `Context: ${context}\n\nPlease help improve the following text while maintaining its core meaning and professional tone. Focus on clarity, conciseness, and proper formatting:\n\n${text}`
            : `Please help improve the following text while maintaining its core meaning and professional tone. Focus on clarity, conciseness, and proper formatting:\n\n${text}`
        }
      ],
      model: "openai/gpt-oss-120b",
      temperature: 0.7,
      max_tokens: 4096,
      top_p: 1,
      stream: false
    });

    const suggestion = completion.choices[0]?.message?.content;

    if (!suggestion) {
      throw new Error('No suggestion generated from AI service');
    }

    return { suggestion };
  } catch (error: any) {
    console.error('Error getting AI suggestion:', error);
    
    let errorMessage = 'Failed to get AI suggestion';
    
    if (error.message?.includes('API key')) {
      errorMessage = 'AI service is not properly configured. Please check the API key.';
    } else if (error.message?.includes('rate limit')) {
      errorMessage = 'AI service rate limit exceeded. Please try again in a moment.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'AI service request timed out. Please try again.';
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { error: errorMessage };
  }
};

export const formatDocument = async (text: string): Promise<AIResponse> => {
  try {
    if (!text || text.trim().length === 0) {
      throw new Error('Please provide text to format');
    }

    if (text.length > 32000) {
      throw new Error('Text is too long. Please use shorter content.');
    }

    const groq = getGroqClient();
    
    const completion = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are DocHelper AI, a professional document formatter focused on improving document structure and readability. Apply proper spacing, paragraphs, and formatting."
        },
        {
          role: "user",
          content: `Please format the following text to improve its structure and readability. Apply proper spacing, paragraphs, and formatting:\n\n${text}`
        }
      ],
      model: "openai/gpt-oss-20b",
      temperature: 0.5,
      max_tokens: 4096,
      top_p: 1,
      stream: false
    });

    const formattedText = completion.choices[0]?.message?.content;

    if (!formattedText) {
      throw new Error('No formatted text generated');
    }

    return { formattedText };
  } catch (error: any) {
    console.error('Error formatting document:', error);
    
    let errorMessage = 'Failed to format document';
    
    if (error.message?.includes('API key')) {
      errorMessage = 'AI service is not properly configured. Please check the API key.';
    } else if (error.message?.includes('rate limit')) {
      errorMessage = 'AI service rate limit exceeded. Please try again in a moment.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'AI service request timed out. Please try again.';
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { error: errorMessage };
  }
};
export const summarizeDocument = async (text: string, length: 'short' | 'medium' | 'long' = 'medium'): Promise<AIResponse> => {
  try {
    if (!text || text.trim().length === 0) {
      throw new Error('Please provide text to summarize');
    }

    if (text.length > 32000) {
      throw new Error('Text is too long. Please use shorter content.');
    }

    const groq = getGroqClient();

    let summaryLength = 'a concise summary (3-5 sentences)';
    if (length === 'short') {
      summaryLength = 'a brief summary (1-2 sentences)';
    } else if (length === 'long') {
      summaryLength = 'a detailed summary (8-10 sentences with key points)';
    }

    const completion = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: "You are DocHelper AI, a professional document summarizer. Create clear, accurate summaries that capture the essential points and main ideas of documents."
        },
        {
          role: "user",
          content: `Please create ${summaryLength} of the following document:\n\n${text}`
        }
      ],
      model: "openai/gpt-oss-120b",
      temperature: 0.5,
      max_tokens: 2048,
      top_p: 1,
      stream: false
    });

    const summary = completion.choices[0]?.message?.content;

    if (!summary) {
      throw new Error('No summary generated');
    }

    return { summary };
  } catch (error: any) {
    console.error('Error summarizing document:', error);

    let errorMessage = 'Failed to summarize document';

    if (error.message?.includes('API key')) {
      errorMessage = 'AI service is not properly configured. Please check the API key.';
    } else if (error.message?.includes('rate limit')) {
      errorMessage = 'AI service rate limit exceeded. Please try again in a moment.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'AI service request timed out. Please try again.';
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorMessage = 'Network error. Please check your internet connection and try again.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { error: errorMessage };
  }
};
